import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import partners from '../data/partners.json';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';

export default function About() {
  return (
    <>
      <Helmet>
        <title>{buildTitle('About')}</title>
        <meta
          name="description"
          content="Meet Sista Kyerewaa, community educator and nurse supporting African families with integration, culture, and life-in-Germany guidance."
        />
        <meta property="og:title" content={buildTitle('About')} />
        <meta
          property="og:description"
          content="Community educator and nurse supporting African families with integration, culture, and life-in-Germany guidance."
        />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={buildTitle('About')} />
        <meta property="twitter:description" content="Community educator and nurse supporting African families with integration, culture, and life-in-Germany guidance." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <link rel="canonical" href={`${SITE_URL}/about`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Sista Kyerewaa",
            "url": `${SITE_URL}/about`,
            "image": `${SITE_URL}/images/sistakyerewaa-hero.png`,
            "jobTitle": "Community Educator & Registered Nurse",
            "description": "Sista Kyerewaa is a Ghanaian community educator and registered nurse based in Germany, helping African and Ghanaian families navigate integration, health, and everyday life in Germany with warmth, faith, and cultural authenticity.",
            "knowsAbout": [
              "German immigration and integration",
              "Healthcare and wellness for African families",
              "Vegan African cooking",
              "Cultural guidance for diaspora communities",
              "German language learning"
            ],
            "sameAs": [
              "https://www.tiktok.com/@sistakyerewaa",
              "https://linktr.ee/sistakyerewaa"
            ],
            "worksFor": {
              "@type": "Organization",
              "name": "Sista Kyerewaa",
              "url": SITE_URL
            }
          })}
        </script>
      </Helmet>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7b7671]">About</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">About Sista Kyerewaa</h1>
        </div>
        <div className="flex flex-col items-center mb-10">
          <div className="border border-stone-200 bg-white/70 p-4 shadow-sm">
            <img
              src="/images/sistakyerewaa-hero.png"
              alt="Sista Kyerewaa, Ghanaian community educator and registered nurse based in Germany"
              className="w-full max-w-xs object-cover"
              loading="lazy"
            />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7b7671] mt-4">sistakyerewaa</p>
        </div>
        <p className="text-lg text-[#5f5b56] mb-6">
          Welcome! I’m Sista Kyerewaa — a passionate Community Educator, Registered Nurse, Creator,
          and cultural bridge‑builder dedicated to inspiring, educating, and uplifting African and
          Ghanaian communities navigating life in Germany.
        </p>
        <p className="text-[#6a6661] mb-8">
          With warmth, authenticity, and a deep love for both my heritage and my new home, I bring my
          professional experience as a healthcare worker and my lived journey as a Ghanaian in Germany
          into every piece of content — blending practical guidance with heart‑led encouragement.
        </p>

        <div className="border border-stone-200 bg-white/70 p-6 mb-10">
          <h2 className="font-display text-2xl mb-4 text-[#2d2b29]">What Sista Kyerewaa Stands For</h2>
          <div className="space-y-4 text-[#6a6661]">
            <div>
              <h3 className="font-display text-xl text-[#2d2b29] mb-2">Community Education & Integration</h3>
              <p>
                Helping African and Ghanaian families make sense of daily life in Germany — from
                navigating culture and systems to finding a sense of belonging and purpose.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-[#2d2b29] mb-2">Healthcare Insight & Wellness</h3>
              <p>
                Nurse‑led guidance that makes health topics clear, compassionate, and culturally relevant.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-[#2d2b29] mb-2">Culinary Creativity</h3>
              <p>
                In the Veggie Kitchen series, I celebrate vegan and vegetarian African‑inspired cooking
                that blends tradition with modern, plant‑based living.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl text-[#2d2b29] mb-2">Cultural Storytelling & Inspiration</h3>
              <p>
                Heartfelt reflections, practical life‑in‑Germany guides, and joyful celebration of African identity.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-stone-200 bg-white/70 p-6 mb-10">
          <h2 className="font-display text-2xl mb-4 text-[#2d2b29]">Services</h2>
          <div className="grid gap-4 md:grid-cols-3 text-[#6a6661]">
            <div className="border border-stone-200 bg-white/80 p-4">
              <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Request pricing</p>
              <h3 className="font-display text-xl text-[#2d2b29] mt-3">1‑on‑1 Consultation</h3>
              <p className="mt-2">Personal guidance for life in Germany, wellness, and next steps.</p>
            </div>
            <div className="border border-stone-200 bg-white/80 p-4">
              <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Starting at €3 per page</p>
              <h3 className="font-display text-xl text-[#2d2b29] mt-3">Document Support</h3>
              <p className="mt-2">Help filling forms and organizing requirements. Educational support only.</p>
            </div>
            <div className="border border-stone-200 bg-white/80 p-4">
              <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Request pricing</p>
              <h3 className="font-display text-xl text-[#2d2b29] mt-3">Speaking & Workshops</h3>
              <p className="mt-2">Invite Sista Kyerewaa to speak at your event or community program.</p>
            </div>
          </div>
        </div>

        <div className="border border-stone-200 bg-white/70 p-6 mb-10">
          <h2 className="font-display text-2xl mb-4 text-[#2d2b29]">Brand collaborations</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {partners.map((partner) => (
              <a
                key={partner.name}
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-stone-200 bg-white/80 p-4 hover:border-[#bdb7b0] transition-colors"
              >
                <img src={partner.logo} alt={`${partner.name} logo`} className="w-full h-20 object-contain" />
                <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671] mt-3">{partner.role}</p>
                <h3 className="font-display text-xl text-[#2d2b29] mt-2">{partner.name}</h3>
                <p className="text-[#6a6661] mt-2">{partner.summary}</p>
              </a>
            ))}
          </div>
        </div>

        <div className="border border-stone-200 bg-white/70 p-6 mb-10">
          <h2 className="font-display text-2xl mb-3 text-[#2d2b29]">Core Values</h2>
          <div className="grid gap-3 sm:grid-cols-2 text-[#6a6661]">
            <p>Community before competition</p>
            <p>Health is wealth</p>
            <p>Faith guides everything</p>
            <p>Culture is strength</p>
            <p>Knowledge should be simple and accessible</p>
            <p>We rise by lifting others</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
          <a href="https://www.tiktok.com/@sistakyerewaa" target="_blank" rel="noopener noreferrer" className="bg-[#2d2b29] text-[#f5f4f2] px-8 py-3 uppercase tracking-[0.25em] text-xs hover:bg-[#1f1d1b] transition-colors">Follow on TikTok</a>
          <a href="https://linktr.ee/sistakyerewaa" target="_blank" rel="noopener noreferrer" className="border border-[#c9c5bf] text-[#2d2b29] px-8 py-3 uppercase tracking-[0.25em] text-xs hover:bg-[#e7e4df] transition-colors">Explore Linktree</a>
          <a href="/book" className="border border-[#c9c5bf] text-[#2d2b29] px-8 py-3 uppercase tracking-[0.25em] text-xs hover:bg-[#e7e4df] transition-colors">Book a Session</a>
          <a href="/book" className="border border-[#c9c5bf] text-[#2d2b29] px-8 py-3 uppercase tracking-[0.25em] text-xs hover:bg-[#e7e4df] transition-colors">Request Document Support</a>
        </div>

        <div className="border border-stone-200 bg-white/70 p-6">
          <h2 className="font-display text-2xl mb-2 text-[#2d2b29]">Connect with the community</h2>
          <p className="text-[#6a6661]">
            Reach out via the <Link to="/book" className="text-[#2d2b29] underline">booking page</Link> or connect on social media.
          </p>
        </div>
      </main>
    </>
  );
}
